import { useQueryClient } from '@tanstack/react-query'

import {
  getGetPizzaIngredientsQueryKey,
  useDeletePizzaIngredientsIngredientId,
  useGetPizzaIngredients,
  useGetStockItemsList,
  usePatchPizzaIngredientsIngredientId,
  usePostPizzaIngredients,
} from '@agos/api-client/menu'
import type { PizzaIngredient, StockItemOption } from '@agos/api-client/model'
import {
  T,
  formatMoney,
  ingredientForSize,
  tt,
  type PizzaIngredientLike,
  type PizzaSize,
} from '@agos/shared'
import { ConfirmDialog, cn, toast, BUTTON_PRIMARY} from '@agos/ui'
import { Loader2, Pencil, Plus, Search, Trash2, X } from 'lucide-react'
import { useMemo, useState } from 'react'

import { buildSizePayload, emptySizeEntry, type SizeFormEntry } from './size-payload'

/**
 * Raw Slovak keys, matched against `pizza_ingredients.category` in the
 * database. They are filter values, not display strings — renaming one here
 * would stop matching rows.
 *
 * `Okraj` joins them for §9.61: the crust is an ingredient category now, and until this list carried
 * it there was no way to create one. The entry claimed the row was *"the owner's to create in the
 * ingredient editor they already use"* and that was not true — the editor offered four categories and
 * this was not among them, so the feature was unreachable from the app.
 */
// Rendered through <T> at the call sites; translating them *here* would stop
// them matching any row, so the rule is off for this line only.
// eslint-disable-next-line no-restricted-syntax
const CATEGORIES = ['Základ', 'Okraj', 'Syr', 'Mäso', 'Zelenina']

const CELL_INPUT =
  'px-2 py-1 rounded-lg bg-background border border-border text-xs outline-none focus:ring-2 focus:ring-primary/20'

type SizeEntries = Record<string, SizeFormEntry>

function StockItemPicker({
  stockItems,
  value,
  onSelect,
}: {
  stockItems: StockItemOption[]
  value: number | null
  onSelect: (item: StockItemOption | null) => void
}) {
  const [query, setQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)

  const matches = useMemo(() => {
    if (!query) return stockItems
    const needle = query.toLowerCase()
    return stockItems.filter(
      (item) =>
        item.name?.toLowerCase().includes(needle) || item.category?.toLowerCase().includes(needle),
    )
  }, [stockItems, query])

  const selected = value ? stockItems.find((item) => item.id === value) : null

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground pointer-events-none" />
        <input
          type="text"
          value={isOpen ? query : (selected?.name ?? '')}
          onChange={(event) => {
            setQuery(event.target.value)
            if (!isOpen) setIsOpen(true)
          }}
          onFocus={() => {
            setIsOpen(true)
            setQuery('')
          }}
          onBlur={() => setTimeout(() => setIsOpen(false), 150)}
          placeholder={tt('Sklad. položka...')}
          className={cn(
            'w-full pl-6 pr-2 py-1 rounded-lg bg-background border text-xs outline-none focus:ring-2 focus:ring-primary/20 transition-all',
            value ? 'border-primary/40 text-foreground' : 'border-border text-muted-foreground',
          )}
        />
      </div>
      {isOpen && matches.length > 0 && (
        <div className="absolute top-full left-0 mt-1 z-50 w-64 bg-popover border border-border rounded-xl shadow-lg max-h-48 overflow-y-auto">
          {value && (
            <button
              type="button"
              onMouseDown={(event) => {
                event.preventDefault()
                onSelect(null)
                setIsOpen(false)
              }}
              className="w-full text-left px-3 py-2 text-xs text-red-400 hover:bg-red-500/10 transition-colors"
            >
              <T>Zrušiť výber</T>
            </button>
          )}
          {matches.map((item) => (
            <button
              key={item.id}
              type="button"
              onMouseDown={(event) => {
                event.preventDefault()
                onSelect(item)
                setIsOpen(false)
              }}
              className={cn(
                'w-full text-left px-3 py-2 text-xs hover:bg-muted/50 transition-colors flex justify-between items-center',
                item.id === value && 'bg-primary/10 text-primary font-semibold',
              )}
            >
              <span>
                <T>{item.name}</T>
              </span>
              <span className="text-muted-foreground text-[10px]">
                {item.category} · {item.unit}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export function ToppingsTab({ sizes }: { sizes: PizzaSize[] }) {
  const queryClient = useQueryClient()
  const { data: ingredients, isLoading } = useGetPizzaIngredients()
  const { data: stockItems } = useGetStockItemsList()

  const createIngredient = usePostPizzaIngredients()
  const updateIngredient = usePatchPizzaIngredientsIngredientId()
  const deleteIngredient = useDeletePizzaIngredientsIngredientId()

  const [editingId, setEditingId] = useState<number | null>(null)
  const [editName, setEditName] = useState('')
  const [editAllergens, setEditAllergens] = useState('')
  const [editStockItemId, setEditStockItemId] = useState<number | null>(null)
  const [editSizes, setEditSizes] = useState<SizeEntries>({})
  const [pendingDeletion, setPendingDeletion] = useState<PizzaIngredient | null>(null)

  const [newName, setNewName] = useState('')
  const [newCategory, setNewCategory] = useState(CATEGORIES[1] ?? 'Syr')
  const [newAllergens, setNewAllergens] = useState('')
  const [newStockItemId, setNewStockItemId] = useState<number | null>(null)
  const [newSizes, setNewSizes] = useState<SizeEntries>({})
  const [addError, setAddError] = useState('')

  /**
   * §9.175 — this read `() => getGetPizzaIngredientsQueryKey()`, which **builds a key** and returns
   * it. Nothing invalidated, so no mutation on this screen ever showed: the toggle snapped back, an
   * added topping was invisible until reload, and a deleted one stayed on the list. `void refresh()`
   * at each call site is what made it look deliberate — the `void` reads as discarding a promise,
   * and there was never a promise.
   */
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: getGetPizzaIngredientsQueryKey() })

  /**
   * The categories to draw: the known ones, plus any the data actually holds.
   *
   * **This listed only the four hard-coded names, and an ingredient outside them was invisible
   * here** — not empty, not greyed, absent, while still being charged for on every pizza that used
   * it. Nothing in the editor would have said so. Found on 2026-08-06 while checking whether §9.61's
   * crust could be created at all; it could not, and neither could anything else somebody had put in
   * a category this file did not know.
   */
  const shownCategories = useMemo(() => {
    const found = new Set(
      (ingredients ?? [])
        .map((ingredient) => (ingredient.category ?? '').trim())
        .filter((category) => category !== ''),
    )
    return [...CATEGORIES, ...[...found].filter((category) => !CATEGORIES.includes(category))]
  }, [ingredients])

  const byCategory = useMemo(() => {
    const grouped: Record<string, PizzaIngredient[]> = {}
    for (const category of shownCategories) {
      grouped[category] = (ingredients ?? []).filter(
        (ingredient) => (ingredient.category ?? '').trim() === category,
      )
    }
    return grouped
  }, [ingredients, shownCategories])

  const startEdit = (ingredient: PizzaIngredient) => {
    setEditingId(ingredient.id ?? null)
    setEditName(ingredient.name ?? '')
    setEditAllergens(ingredient.allergens ?? '')
    setEditStockItemId(ingredient.stockItemId ?? null)

    const entries: SizeEntries = {}
    for (const size of sizes) {
      const values = ingredientForSize(ingredient as PizzaIngredientLike, size.cm)
      const sizeData = (ingredient.sizeData ?? {}) as Record<string, unknown>
      // A size with no exact data is prefilled with its fallback, so that
      // saving writes the inherited number down instead of leaving a hole.
      const isExact = sizeData[String(size.cm)] != null || size.cm === 32 || size.cm === 50

      entries[String(size.cm)] = {
        price: String(values.price ?? 0),
        unitLabel: (isExact ? values.unitLabel : null) ?? '',
        weightG: values.weightG ? String(values.weightG) : '',
        cost: values.cost != null ? String(values.cost) : '',
      }
    }
    setEditSizes(entries)
  }

  const patchEntry = (
    setEntries: (update: (previous: SizeEntries) => SizeEntries) => void,
    cm: number,
    patch: Partial<SizeFormEntry>,
  ) =>
    setEntries((previous) => ({
      ...previous,
      [String(cm)]: { ...(previous[String(cm)] ?? emptySizeEntry()), ...patch },
    }))

  const saveEdit = () => {
    if (editingId === null) return
    updateIngredient.mutate(
      {
        ingredientId: editingId,
        data: {
          name: editName.trim(),
          allergens: editAllergens.trim() || null,
          stockItemId: editStockItemId,
          ...buildSizePayload(editSizes, sizes, true),
        },
      },
      {
        onSuccess: () => {
          setEditingId(null)
          void refresh()
          toast({ title: tt('Uložené') })
        },
      },
    )
  }

  const addIngredient = () => {
    const everySizePriced = sizes.every(
      (size) => (newSizes[String(size.cm)]?.price ?? '').trim() !== '',
    )
    if (!newName.trim()) {
      setAddError(tt('Zadajte názov suroviny.'))
      return
    }
    if (sizes.length === 0 || !everySizePriced) {
      setAddError(tt('Vyplňte cenu pre každú zapnutú veľkosť.'))
      return
    }

    setAddError('')
    createIngredient.mutate(
      {
        data: {
          name: newName.trim(),
          category: newCategory,
          allergens: newAllergens.trim() || null,
          active: true,
          stockItemId: newStockItemId,
          ...buildSizePayload(newSizes, sizes, false),
        },
      },
      {
        onSuccess: () => {
          setNewName('')
          setNewAllergens('')
          setNewStockItemId(null)
          setNewSizes({})
          void refresh()
          toast({ title: tt('Surovina pridaná') })
        },
      },
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {shownCategories.map((category) => {
        const rows = byCategory[category] ?? []
        return (
          <div
            key={category}
            data-ingredient-category={category}
            className="bg-card border border-border rounded-2xl overflow-hidden"
          >
            <div className="px-5 py-3 bg-muted/40 border-b border-border flex items-center justify-between">
              <p className="font-bold text-sm text-foreground">
                <T>{category}</T>
              </p>
              <span className="text-xs text-muted-foreground">
                {rows.length} <T>surovín</T>
              </span>
            </div>

            {rows.length === 0 ? (
              <div className="px-5 py-6 text-center text-sm text-muted-foreground">
                <T>Žiadne suroviny v tejto kategórii</T>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table
                  className="w-full text-sm"
                  style={{ minWidth: `${560 + sizes.length * 220}px` }}
                >
                  <thead>
                    <tr className="text-xs text-muted-foreground border-b border-border">
                      <th className="text-left px-5 py-2 font-semibold">
                        <T>Názov</T>
                      </th>
                      <th className="text-left px-3 py-2 font-semibold">
                        <T>Sklad. položka</T>
                      </th>
                      <th className="text-left px-3 py-2 font-semibold">
                        <T>Alergény</T>
                      </th>
                      {sizes.map((size) => (
                        <th key={`weight-${size.cm}`} className="text-center px-3 py-2 font-semibold">
                          <T>Množ.</T> {size.cm}cm
                        </th>
                      ))}
                      {sizes.map((size) => (
                        <th key={`price-${size.cm}`} className="text-center px-3 py-2 font-semibold">
                          <T>Cena</T> {size.cm} cm
                        </th>
                      ))}
                      {sizes.map((size) => (
                        <th
                          key={`cost-${size.cm}`}
                          className="text-center px-3 py-2 font-semibold"
                          title={tt('Surovinová cena - pre výpočet marže')}
                        >
                          <T>Náklad</T> {size.cm}
                        </th>
                      ))}
                      <th
                        className="text-center px-3 py-2 font-semibold"
                        title={tt('Marža na najmenšej veľkosti (cena − náklad)')}
                      >
                        <T>Marža</T>
                      </th>
                      <th className="text-center px-4 py-2 font-semibold">
                        <T>Aktívna</T>
                      </th>
                      <th className="px-4 py-2" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {rows.map((ingredient) => {
                      const isEditing = editingId === ingredient.id
                      const smallest = sizes[0]
                      const atSmallest = smallest
                        ? ingredientForSize(ingredient as PizzaIngredientLike, smallest.cm)
                        : null
                      const margin =
                        atSmallest && atSmallest.cost != null
                          ? atSmallest.price - atSmallest.cost
                          : null

                      return (
                        <tr key={ingredient.id} className={cn(isEditing && 'bg-primary/5')}>
                          <td className="px-4 py-2">
                            {isEditing ? (
                              <input
                                value={editName}
                                onChange={(event) => setEditName(event.target.value)}
                                aria-label={tt('Názov')}
                                className={cn(CELL_INPUT, 'w-full text-sm')}
                              />
                            ) : (
                              <span className="font-semibold text-foreground">
                                <T>{ingredient.name}</T>
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            {isEditing ? (
                              <StockItemPicker
                                stockItems={stockItems ?? []}
                                value={editStockItemId}
                                onSelect={(item) => setEditStockItemId(item?.id ?? null)}
                              />
                            ) : (
                              <span className="text-xs text-muted-foreground">
                                {(stockItems ?? []).find(
                                  (item) => item.id === ingredient.stockItemId,
                                )?.name ?? '-'}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            {isEditing ? (
                              <input
                                value={editAllergens}
                                onChange={(event) => setEditAllergens(event.target.value)}
                                placeholder={tt('Alergény')}
                                className={cn(CELL_INPUT, 'w-24')}
                              />
                            ) : (
                              <span className="text-xs text-muted-foreground">
                                {ingredient.allergens || '-'}
                              </span>
                            )}
                          </td>

                          {sizes.map((size) => {
                            const values = ingredientForSize(
                              ingredient as PizzaIngredientLike,
                              size.cm,
                            )
                            return (
                              <td key={`weight-${size.cm}`} className="px-3 py-2 text-center">
                                {isEditing ? (
                                  <input
                                    value={editSizes[String(size.cm)]?.unitLabel ?? ''}
                                    onChange={(event) =>
                                      patchEntry(setEditSizes, size.cm, {
                                        unitLabel: event.target.value,
                                      })
                                    }
                                    placeholder={tt('napr. 6 dkg')}
                                    aria-label={`${tt('Množstvo')} ${size.cm}`}
                                    className={cn(CELL_INPUT, 'w-20')}
                                  />
                                ) : (
                                  <span className="text-xs text-muted-foreground">
                                    {values.unitLabel ?? '-'}
                                  </span>
                                )}
                              </td>
                            )
                          })}

                          {sizes.map((size) => {
                            const values = ingredientForSize(
                              ingredient as PizzaIngredientLike,
                              size.cm,
                            )
                            return (
                              <td key={`price-${size.cm}`} className="px-3 py-2 text-center">
                                {isEditing ? (
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={editSizes[String(size.cm)]?.price ?? ''}
                                    onChange={(event) =>
                                      patchEntry(setEditSizes, size.cm, {
                                        price: event.target.value,
                                      })
                                    }
                                    aria-label={`${tt('Cena')} ${size.cm}`}
                                    className={cn(CELL_INPUT, 'w-20')}
                                  />
                                ) : (
                                  <span className="font-semibold">{formatMoney(values.price)}</span>
                                )}
                              </td>
                            )
                          })}

                          {sizes.map((size) => {
                            const values = ingredientForSize(
                              ingredient as PizzaIngredientLike,
                              size.cm,
                            )
                            return (
                              <td key={`cost-${size.cm}`} className="px-3 py-2 text-center">
                                {isEditing ? (
                                  <input
                                    type="number"
                                    step="0.0001"
                                    value={editSizes[String(size.cm)]?.cost ?? ''}
                                    onChange={(event) =>
                                      patchEntry(setEditSizes, size.cm, {
                                        cost: event.target.value,
                                      })
                                    }
                                    aria-label={`${tt('Náklad')} ${size.cm}`}
                                    className={cn(CELL_INPUT, 'w-20')}
                                  />
                                ) : (
                                  <span className="text-xs text-muted-foreground">
                                    {values.cost != null ? formatMoney(values.cost) : '-'}
                                  </span>
                                )}
                              </td>
                            )
                          })}

                          <td className="px-3 py-2 text-center">
                            <span
                              className={cn(
                                'text-xs font-semibold',
                                margin === null
                                  ? 'text-muted-foreground'
                                  : margin > 0
                                    ? 'text-emerald-500'
                                    : 'text-red-400',
                              )}
                            >
                              {margin === null ? '-' : formatMoney(margin)}
                            </span>
                          </td>

                          <td className="px-4 py-2 text-center">
                            <input
                              type="checkbox"
                              checked={Boolean(ingredient.active)}
                              aria-label={tt('Aktívna')}
                              onChange={() =>
                                ingredient.id !== undefined &&
                                updateIngredient.mutate(
                                  {
                                    ingredientId: ingredient.id,
                                    data: { active: !ingredient.active },
                                  },
                                  { onSuccess: () => void refresh() },
                                )
                              }
                              className="w-4 h-4 rounded accent-primary"
                            />
                          </td>

                          <td className="px-4 py-2">
                            <div className="flex items-center justify-end gap-1">
                              {isEditing ? (
                                <>
                                  <button
                                    type="button"
                                    onClick={saveEdit}
                                    disabled={updateIngredient.isPending}
                                    className={cn('px-2 py-1 rounded-lg', BUTTON_PRIMARY, 'text-xs')}
                                  >
                                    <T>Uložiť</T>
                                  </button>
                                  <button
                                    type="button"
                                    aria-label={tt('Zrušiť')}
                                    onClick={() => setEditingId(null)}
                                    className="p-1 rounded-lg text-muted-foreground hover:text-foreground"
                                  >
                                    <X className="w-3.5 h-3.5" />
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    aria-label={tt('Upraviť')}
                                    onClick={() => startEdit(ingredient)}
                                    className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                                  >
                                    <Pencil className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    type="button"
                                    aria-label={tt('Zmazať')}
                                    onClick={() => setPendingDeletion(ingredient)}
                                    className="p-1.5 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )
      })}

      <div className="bg-card border border-dashed border-border rounded-2xl p-5 space-y-3">
        <p className="text-sm font-bold text-foreground flex items-center gap-2">
          <Plus className="w-4 h-4 text-primary" /> <T>Nová surovina</T>
        </p>
        <div className="flex flex-wrap gap-2">
          <input
            value={newName}
            onChange={(event) => setNewName(event.target.value)}
            placeholder={tt('Názov suroviny')}
            className={cn(CELL_INPUT, 'flex-1 min-w-[10rem] text-sm py-2')}
          />
          <select
            value={newCategory}
            onChange={(event) => setNewCategory(event.target.value)}
            aria-label={tt('Kategória')}
            className={cn(CELL_INPUT, 'text-sm py-2')}
          >
            {CATEGORIES.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
          <input
            value={newAllergens}
            onChange={(event) => setNewAllergens(event.target.value)}
            placeholder={tt('Alergény')}
            className={cn(CELL_INPUT, 'w-28 text-sm py-2')}
          />
          <div className="w-48">
            <StockItemPicker
              stockItems={stockItems ?? []}
              value={newStockItemId}
              onSelect={(item) => setNewStockItemId(item?.id ?? null)}
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {sizes.map((size) => (
            <input
              key={size.cm}
              type="number"
              step="0.01"
              value={newSizes[String(size.cm)]?.price ?? ''}
              onChange={(event) => patchEntry(setNewSizes, size.cm, { price: event.target.value })}
              placeholder={`${tt('Cena')} ${size.cm} cm`}
              aria-label={`${tt('Cena')} ${size.cm} cm`}
              className={cn(CELL_INPUT, 'w-32 text-sm py-2')}
            />
          ))}
        </div>
        {addError && <p className="text-xs text-destructive">{addError}</p>}
        <button
          type="button"
          onClick={addIngredient}
          disabled={createIngredient.isPending}
          className={cn('flex items-center gap-2 px-5 py-2.5 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
        >
          {createIngredient.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Plus className="w-4 h-4" />
          )}
          <T>Pridať surovinu</T>
        </button>
      </div>

      <ConfirmDialog
        open={pendingDeletion !== null}
        onClose={() => setPendingDeletion(null)}
        onConfirm={() => {
          const ingredientId = pendingDeletion?.id
          if (ingredientId === undefined) return
          deleteIngredient.mutate(
            { ingredientId },
            {
              onSuccess: () => {
                setPendingDeletion(null)
                void refresh()
                toast({ title: tt('Surovina zmazaná') })
              },
            },
          )
        }}
        loading={deleteIngredient.isPending}
        variant="danger"
        title={tt('Zmazať surovinu?')}
        description={`${tt('Surovina')} "${pendingDeletion?.name ?? ''}" ${tt('sa odstráni z pizza builderu.')}`}
        confirmLabel={tt('Zmazať')}
      />
    </div>
  )
}
